import { ref, computed, watch } from "vue";
import { supabase, useSupabase } from "../db/supabaseClient.js";
import { rangeFor, previousRange, daySeq, monthKey, monthShort } from "../utils/analyticsUtils.js";

export function useAnalyticsData() {
  const periodo = ref("30d");
  
  // Advanced Filters
  const advStart = ref("");
  const advEnd = ref("");
  const advMethods = ref([]);
  const advMin = ref("");
  const advMax = ref("");
  const advCustomer = ref("");
  const advProduct = ref("");
  const advSupplier = ref("");
  const payMethodOptions = ref([]);

  function activeRange() {
    const r = rangeFor(periodo.value);
    let start, end;

    if (advStart.value) {
      // Parse "YYYY-MM-DD" as local start of day
      const [y, m, d] = advStart.value.split("-").map(Number);
      start = new Date(y, m - 1, d, 0, 0, 0, 0);
    } else {
      start = r.startDate;
    }

    if (advEnd.value) {
      // Parse "YYYY-MM-DD" as local end of day
      const [y, m, d] = advEnd.value.split("-").map(Number);
      end = new Date(y, m - 1, d, 23, 59, 59, 999);
    } else {
      end = r.endDate;
    }

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      startDate: start,
      endDate: end,
    };
  }

  function clearAdvancedFilters() {
    advStart.value = "";
    advEnd.value = "";
    advMethods.value = [];
    advMin.value = "";
    advMax.value = "";
    advCustomer.value = "";
    advSupplier.value = "";
    advProduct.value = "";
  }

  // KPIs
  const kpis = ref({
    ventas: 0,
    ingresos: 0,
    ingresosVar: 0,
    ventasVar: 0,
    compras: 0,
    comprasImporte: 0,
    comprasVar: 0,
    impuestos: 0,
  });

  function clampPercent(v) {
    const n = Number(v || 0);
    if (!isFinite(n) || n <= 0) return 0;
    return Math.min(100, n);
  }

  const clientsCount = ref(0);
  const ordersCount = ref(0);

  // Charts Data
  const salesOverviewData = ref([]);
  const methodLegend = ref([]);
  const cashFlowDays = ref([]);
  const weeklySubscribers = ref([
    { day: "Dom", value: 0 },
    { day: "Lun", value: 0 },
    { day: "Mar", value: 0 },
    { day: "Mié", value: 0 },
    { day: "Jue", value: 0 },
    { day: "Vie", value: 0 },
    { day: "Sáb", value: 0 },
  ]);
  const salesDistribution = ref([]);
  const activity = ref([]);
  const bubbleDays = ref([]);

  // Series
  const salesDaily = ref([]);
  const salesMonthly = ref([]);
  const salesYearly = ref([]);

  // Details
  const salesDetails = ref([]);
  const purchasesDetails = ref([]);

  // Computed Helpers
  const maxStack = computed(() => {
    const totals = salesOverviewData.value.map((m) =>
      (m.values || []).reduce((s, v) => s + Number(v || 0), 0)
    );
    return Math.max(1, ...totals);
  });

  const maxAbsFlow = computed(() =>
    Math.max(
      1,
      ...cashFlowDays.value.flatMap((d) => [
        Math.abs(Number(d.income || 0)),
        Math.abs(Number(d.expense || 0)),
      ])
    )
  );

  const maxWeek = computed(() =>
    Math.max(1, ...weeklySubscribers.value.map((d) => Number(d.value || 0)))
  );

  const maxBubble = computed(() =>
    Math.max(1, ...bubbleDays.value.map((d) => Number(d.amt || 0)))
  );

  const maxSalesDaily = computed(() =>
    Math.max(1, ...salesDaily.value.map((d) => Number(d.total || 0)))
  );
  const maxSalesMonthly = computed(() =>
    Math.max(1, ...salesMonthly.value.map((d) => Number(d.total || 0)))
  );
  const maxSalesYearly = computed(() =>
    Math.max(1, ...salesYearly.value.map((d) => Number(d.total || 0)))
  );

  const filteredSalesDetails = computed(() => {
    let list = salesDetails.value.slice();
    const min = advMin.value ? Number(advMin.value) : null;
    const max = advMax.value ? Number(advMax.value) : null;
    if (min != null) list = list.filter((r) => Number(r.total || 0) >= min);
    if (max != null) list = list.filter((r) => Number(r.total || 0) <= max);
    const cq = advCustomer.value.trim().toLowerCase();
    if (cq)
      list = list.filter((r) =>
        String(r.customer || "")
          .toLowerCase()
          .includes(cq)
      );
    const pq = advProduct.value.trim().toLowerCase();
    if (pq)
      list = list.filter((r) =>
        String(r.product || "")
          .toLowerCase()
          .includes(pq)
      );
    return list;
  });

  const filteredPurchasesDetails = computed(() => {
    let list = purchasesDetails.value.slice();
    const min = advMin.value ? Number(advMin.value) : null;
    const max = advMax.value ? Number(advMax.value) : null;
    if (min != null) list = list.filter((r) => Number(r.total || 0) >= min);
    if (max != null) list = list.filter((r) => Number(r.total || 0) <= max);
    const sq = advSupplier.value.trim().toLowerCase();
    if (sq)
      list = list.filter((r) =>
        String(r.supplier || "")
          .toLowerCase()
          .includes(sq)
      );
    const pq = advProduct.value.trim().toLowerCase();
    if (pq)
      list = list.filter((r) =>
        String(r.product || "")
          .toLowerCase()
          .includes(pq)
      );
    return list;
  });

  // Load Functions
  async function loadPaymentMethods() {
    console.log("DEBUG: loadPaymentMethods called");
    if (!useSupabase()) {
      console.error("DEBUG: useSupabase returned false");
      return;
    }
    const r = activeRange();
    console.log("DEBUG: activeRange", r);
    const { data } = await supabase
      .from("sales")
      .select("payment_method")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    const set = new Set(
      (data || []).map((d) => String(d.payment_method || "")).filter(Boolean)
    );
    payMethodOptions.value = Array.from(set);
  }

  async function loadSalesDailyRange() {
    if (!useSupabase()) return;
    const r = activeRange();
    let qs = supabase
      .from("sales")
      .select("id_sales,total,payment_method,customer_id,created_at")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    if (advMethods.value && advMethods.value.length)
      qs = qs.in("payment_method", advMethods.value);
    const { data, error } = await qs;
    if (error) {
      console.error("loadSalesDailyRange error:", error);
    }
    let rows = Array.isArray(data) ? data : [];
    
    const minV = advMin.value ? Number(advMin.value) : null;
    const maxV = advMax.value ? Number(advMax.value) : null;
    if (minV != null) rows = rows.filter((s) => Number(s.total || 0) >= minV);
    if (maxV != null) rows = rows.filter((s) => Number(s.total || 0) <= maxV);
    const map = new Map();
    rows.forEach((row) => {
      const k = new Date(row.created_at).toISOString().slice(0, 10);
      map.set(k, (map.get(k) || 0) + Number(row.total || 0));
    });
    
    const days = daySeq(r.startDate, r.endDate);
    salesDaily.value = days.map((d) => {
      const k = d.toISOString().slice(0, 10);
      return { date: k, total: Number(map.get(k) || 0) };
    });
  }

  async function loadSalesMonthlyLast12() {
    if (!useSupabase()) return;
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    let qs = supabase
      .from("sales")
      .select("total,created_at,payment_method")
      .gte("created_at", start.toISOString())
      .lte("created_at", end.toISOString());
    if (advMethods.value && advMethods.value.length)
      qs = qs.in("payment_method", advMethods.value);
    const { data } = await qs;
    let rows = Array.isArray(data) ? data : [];
    const minV = advMin.value ? Number(advMin.value) : null;
    const maxV = advMax.value ? Number(advMax.value) : null;
    if (minV != null) rows = rows.filter((s) => Number(s.total || 0) >= minV);
    if (maxV != null) rows = rows.filter((s) => Number(s.total || 0) <= maxV);
    const map = new Map();
    rows.forEach((row) => {
      const d = new Date(row.created_at);
      const key = monthKey(new Date(d.getFullYear(), d.getMonth(), 1));
      map.set(key, (map.get(key) || 0) + Number(row.total || 0));
    });
    const out = [];
    for (let i = 11; i >= 0; i--) {
      const base = new Date(end.getFullYear(), end.getMonth() - i, 1);
      const key = monthKey(base);
      out.push({
        key,
        label: `${monthShort(base)} ${String(base.getFullYear()).slice(-2)}`,
        total: Number(map.get(key) || 0),
      });
    }
    salesMonthly.value = out;
  }

  async function loadSalesYearlyLast5() {
    if (!useSupabase()) return;
    const end = new Date();
    const start = new Date(end.getFullYear() - 4, 0, 1);
    let qs = supabase
      .from("sales")
      .select("total,created_at,payment_method")
      .gte("created_at", start.toISOString())
      .lte("created_at", end.toISOString());
    if (advMethods.value && advMethods.value.length)
      qs = qs.in("payment_method", advMethods.value);
    const { data } = await qs;
    let rows = Array.isArray(data) ? data : [];
    const minV = advMin.value ? Number(advMin.value) : null;
    const maxV = advMax.value ? Number(advMax.value) : null;
    if (minV != null) rows = rows.filter((s) => Number(s.total || 0) >= minV);
    if (maxV != null) rows = rows.filter((s) => Number(s.total || 0) <= maxV);
    const map = new Map();
    rows.forEach((row) => {
      const y = new Date(row.created_at).getFullYear();
      map.set(y, (map.get(y) || 0) + Number(row.total || 0));
    });
    const out = [];
    for (let y = end.getFullYear() - 4; y <= end.getFullYear(); y++) {
      out.push({ year: y, total: Number(map.get(y) || 0) });
    }
    salesYearly.value = out;
  }

  async function loadSalesSeries() {
    await loadSalesDailyRange();
    await loadSalesMonthlyLast12();
    await loadSalesYearlyLast5();
  }

  async function loadAnalytics() {
    if (!useSupabase()) return;
    const r = activeRange();
    const pr = previousRange(r);

    let qCur = supabase
      .from("sales")
      .select("total,tax_total,payment_method,created_at")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    if (advMethods.value && advMethods.value.length)
      qCur = qCur.in("payment_method", advMethods.value);
    const { data: salesRows } = await qCur;
    let curSales = Array.isArray(salesRows) ? salesRows : [];
    const minV = advMin.value ? Number(advMin.value) : null;
    const maxV = advMax.value ? Number(advMax.value) : null;
    if (minV != null)
      curSales = curSales.filter((s) => Number(s.total || 0) >= minV);
    if (maxV != null)
      curSales = curSales.filter((s) => Number(s.total || 0) <= maxV);
    kpis.value.ventas = curSales.length;
    kpis.value.ingresos = curSales.reduce((s, r) => s + Number(r.total || 0), 0);
    kpis.value.impuestos = curSales.reduce(
      (s, r) => s + Number(r.tax_total || 0),
      0
    );

    let qPrev = supabase
      .from("sales")
      .select("total")
      .gte("created_at", pr.start)
      .lte("created_at", pr.end);
    if (advMethods.value && advMethods.value.length)
      qPrev = qPrev.in("payment_method", advMethods.value);
    const { data: prevSales } = await qPrev;
    const prevSalesRows = Array.isArray(prevSales) ? prevSales : [];
    const prevCount = prevSalesRows.length;
    const prevRevenue = prevSalesRows.reduce(
      (s, r) => s + Number(r.total || 0),
      0
    );
    const ventasVarRaw = prevCount
      ? (kpis.value.ventas / prevCount) * 100
      : (kpis.value.ventas > 0 ? 100 : 0);
    const ingresosVarRaw = prevRevenue
      ? (kpis.value.ingresos / prevRevenue) * 100
      : (kpis.value.ingresos > 0 ? 100 : 0);
    kpis.value.ventasVar = clampPercent(ventasVarRaw);
    kpis.value.ingresosVar = clampPercent(ingresosVarRaw);

    const dist = new Map();
    curSales.forEach((r) => {
      const key = String(r.payment_method || "Otros");
      dist.set(key, (dist.get(key) || 0) + Number(r.total || 0));
    });
    salesDistribution.value = Array.from(dist.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, amount]) => ({ label, amount }));

    const weekMap = new Map([
      ["Dom", 0],
      ["Lun", 0],
      ["Mar", 0],
      ["Mié", 0],
      ["Jue", 0],
      ["Vie", 0],
      ["Sáb", 0],
    ]);
    curSales.forEach((r) => {
      const d = new Date(r.created_at);
      const label = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][d.getDay()];
      weekMap.set(label, (weekMap.get(label) || 0) + Number(r.total || 0));
    });
    weeklySubscribers.value = Array.from(weekMap.entries()).map(
      ([day, value]) => ({ day, value })
    );

    try {
      const { data: purRows } = await supabase
        .from("purchases")
        .select("total,created_at")
        .gte("created_at", r.start)
        .lte("created_at", r.end);
      const curPurch = Array.isArray(purRows) ? purRows : [];
      kpis.value.compras = curPurch.length;
      kpis.value.comprasImporte = curPurch.reduce(
        (s, r) => s + Number(r.total || 0),
        0
      );
      const { data: prevPurch } = await supabase
        .from("purchases")
        .select("id_purchases")
        .gte("created_at", pr.start)
        .lte("created_at", pr.end);
      const prevPC = Array.isArray(prevPurch) ? prevPurch.length : 0;
      const comprasVarRaw = prevPC
        ? (kpis.value.compras / prevPC) * 100
        : (kpis.value.compras > 0 ? 100 : 0);
      kpis.value.comprasVar = clampPercent(comprasVarRaw);
    } catch {}
    try {
      const { count: clCount } = await supabase
        .from("customers")
        .select("*", { count: "exact", head: true });
      clientsCount.value = clCount || 0;
    } catch {}
    try {
      const { count: soCount } = await supabase
        .from("sales")
        .select("*", { count: "exact", head: true })
        .gte("created_at", r.start)
        .lte("created_at", r.end);
      ordersCount.value = soCount ?? kpis.value.ventas;
    } catch {}
  }

  async function loadMonthlyStacked() {
    if (!useSupabase()) return;
    const now = new Date();
    function monthRange(offset) {
      const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const start = new Date(base.getFullYear(), base.getMonth(), 1);
      const end = new Date(
        base.getFullYear(),
        base.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      return {
        start: start.toISOString(),
        end: end.toISOString(),
        label: [
          "Ene",
          "Feb",
          "Mar",
          "Abr",
          "May",
          "Jun",
          "Jul",
          "Ago",
          "Sep",
          "Oct",
          "Nov",
          "Dic",
        ][start.getMonth()],
      };
    }
    const ranges = [monthRange(-2), monthRange(-1), monthRange(0)];
    const earliest = ranges[0].start;
    const latest = ranges[2].end;
    const { data: rows } = await supabase
      .from("sales")
      .select("total,payment_method,created_at")
      .gte("created_at", earliest)
      .lte("created_at", latest);
    const byMonth = new Map(ranges.map((r) => [r.label, new Map()]));
    (rows || []).forEach((r) => {
      const d = new Date(r.created_at);
      const label = [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
      ][d.getMonth()];
      if (!byMonth.has(label)) return;
      const m = byMonth.get(label);
      const key = String(r.payment_method || "Otros");
      m.set(key, (m.get(key) || 0) + Number(r.total || 0));
    });
    const global = new Map();
    byMonth.forEach((m) =>
      m.forEach((v, k) => global.set(k, (global.get(k) || 0) + v))
    );
    const methods = Array.from(global.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([k]) => k);
    methodLegend.value = methods;
    salesOverviewData.value = ranges.map((r) => {
      const m = byMonth.get(r.label);
      const values = methods.map((k) => Number(m.get(k) || 0));
      return { label: r.label, values };
    });
  }

  async function loadDailyFlow() {
    if (!useSupabase()) return;
    const r = activeRange();
    let qs = supabase
      .from("sales")
      .select("total,created_at")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    if (advMethods.value && advMethods.value.length)
      qs = qs.in("payment_method", advMethods.value);
    const { data: srows } = await qs;
    const { data: prows } = await supabase
      .from("purchases")
      .select("total,created_at")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    const smap = new Map();
    (srows || []).forEach((r) => {
      const k = new Date(r.created_at).toISOString().slice(0, 10);
      smap.set(k, (smap.get(k) || 0) + Number(r.total || 0));
    });
    const pmap = new Map();
    (prows || []).forEach((r) => {
      const k = new Date(r.created_at).toISOString().slice(0, 10);
      pmap.set(k, (pmap.get(k) || 0) + Number(r.total || 0));
    });
    cashFlowDays.value = daySeq(r.startDate, r.endDate).map((d) => {
      const k = d.toISOString().slice(0, 10);
      return {
        date: k,
        income: Number(smap.get(k) || 0),
        expense: Number(pmap.get(k) || 0),
      };
    });
    bubbleDays.value = cashFlowDays.value.map((d) => ({
      date: d.date,
      amt: Number(d.income || 0),
    }));
  }

  async function loadRecentActivity() {
    if (!useSupabase()) return;
    const { data: s } = await supabase
      .from("sales")
      .select("id_sales,total,payment_method,created_at")
      .order("created_at", { ascending: false })
      .limit(10);
    const { data: p } = await supabase
      .from("purchases")
      .select("id_purchases,total,created_at")
      .order("created_at", { ascending: false })
      .limit(10);
    const rows = [
      ...(s || []).map((r) => ({
        id: `s_${r.id_sales}`,
        tipo: "Venta",
        total: Number(r.total || 0),
        metodo: r.payment_method || null,
        fecha: r.created_at,
      })),
      ...(p || []).map((r) => ({
        id: `p_${r.id_purchases}`,
        tipo: "Compra",
        total: Number(r.total || 0),
        metodo: null,
        fecha: r.created_at,
      })),
    ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    activity.value = rows.slice(0, 12);
  }

  async function loadDetailedSales() {
    if (!useSupabase()) return;
    const r = activeRange();
    let q = supabase
      .from("sales")
      .select("id_sales,customer_id,payment_method,created_at,total")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    if (advMethods.value && advMethods.value.length)
      q = q.in("payment_method", advMethods.value);
    const { data: srows } = await q;
    const sales = Array.isArray(srows) ? srows : [];
    const ids = sales.map((s) => s.id_sales);
    if (!ids.length) {
      salesDetails.value = [];
      return;
    }
    const { data: items } = await supabase
      .from("sale_items")
      .select("sale_id,product_id,quantity,unit_price,total")
      .in("sale_id", ids);
    const pids = Array.from(new Set((items || []).map((i) => i.product_id)));
    const cids = Array.from(
      new Set(sales.map((s) => s.customer_id).filter(Boolean))
    );
    const { data: prod } = pids.length
      ? await supabase.from("v_inventory_basic").select("id,name,image_base64,image_mime").in("id", pids)
      : { data: [] };
    const { data: cust } = cids.length
      ? await supabase
          .from("customers")
          .select("id_customers,name")
          .in("id_customers", cids)
      : { data: [] };
    const pmap = new Map((prod || []).map((p) => [p.id, p]));
    const cmap = new Map((cust || []).map((c) => [c.id_customers, c.name]));
    const smap = new Map(sales.map((s) => [s.id_sales, s]));
    salesDetails.value = (items || [])
      .map((i) => {
        const s = smap.get(i.sale_id) || {};
        const productData = pmap.get(i.product_id);
        return {
          date: s.created_at,
          customer: s.customer_id ? cmap.get(s.customer_id) || null : null,
          method: s.payment_method || null,
          product: productData?.name || String(i.product_id),
          product_image: productData?.image_base64 || null,
          product_image_mime: productData?.image_mime || null,
          quantity: Number(i.quantity || 0),
          unit_price: Number(i.unit_price || 0),
          total: Number(i.total || 0),
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  async function loadDetailedPurchases() {
    if (!useSupabase()) return;
    const r = activeRange();
    const { data: prows } = await supabase
      .from("purchases")
      .select("id_purchases,supplier_id,created_at,total")
      .gte("created_at", r.start)
      .lte("created_at", r.end);
    const purchases = Array.isArray(prows) ? prows : [];
    const ids = purchases.map((p) => p.id_purchases);
    if (!ids.length) {
      purchasesDetails.value = [];
      return;
    }
    const { data: items } = await supabase
      .from("purchase_items")
      .select("purchase_id,product_id,quantity,unit_cost,total")
      .in("purchase_id", ids);
    const pids = Array.from(new Set((items || []).map((i) => i.product_id)));
    const sids = Array.from(
      new Set(purchases.map((p) => p.supplier_id).filter(Boolean))
    );
    const { data: prod } = pids.length
      ? await supabase.from("v_inventory_basic").select("id,name,image_base64,image_mime").in("id", pids)
      : { data: [] };
    const { data: sups } = sids.length
      ? await supabase
          .from("suppliers")
          .select("id_suppliers,name")
          .in("id_suppliers", sids)
      : { data: [] };
    const pmap = new Map((prod || []).map((p) => [p.id, p]));
    const smap = new Map((sups || []).map((s) => [s.id_suppliers, s.name]));
    const prmap = new Map(purchases.map((p) => [p.id_purchases, p]));
    purchasesDetails.value = (items || [])
      .map((i) => {
        const p = prmap.get(i.purchase_id) || {};
        const productData = pmap.get(i.product_id);
        return {
          date: p.created_at,
          supplier: p.supplier_id ? smap.get(p.supplier_id) || null : null,
          product: productData?.name || String(i.product_id),
          product_image: productData?.image_base64 || null,
          product_image_mime: productData?.image_mime || null,
          quantity: Number(i.quantity || 0),
          unit_cost: Number(i.unit_cost || 0),
          total: Number(i.total || 0),
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  return {
    periodo,
    activeRange,
    clearAdvancedFilters,
    advStart,
    advEnd,
    advMethods,
    advMin,
    advMax,
    advCustomer,
    advProduct,
    advSupplier,
    payMethodOptions,
    kpis,
    clientsCount,
    ordersCount,
    salesOverviewData,
    methodLegend,
    cashFlowDays,
    weeklySubscribers,
    salesDistribution,
    activity,
    bubbleDays,
    salesDaily,
    salesMonthly,
    salesYearly,
    salesDetails,
    purchasesDetails,
    filteredSalesDetails,
    filteredPurchasesDetails,
    maxStack,
    maxAbsFlow,
    maxWeek,
    maxBubble,
    maxSalesDaily,
    maxSalesMonthly,
    maxSalesYearly,
    loadPaymentMethods,
    loadSalesSeries,
    loadAnalytics,
    loadMonthlyStacked,
    loadDailyFlow,
    loadRecentActivity,
    loadDetailedSales,
    loadDetailedPurchases,
  };
}
